// src/config/api.js
// API base URL resolution — deliberately does NOT rely on import.meta.env.PROD.
// The Vercel production build was resolving PROD to false, so API_URL fell back
// to '' (relative), making the deployed site call same-origin /api/* (which the
// SPA rewrite turns into a 404 -> "Resource not found"). Resolution order:
//   1. VITE_API_URL if explicitly set (e.g. configured in Vercel env)
//   2. the Railway backend when running on a *.518cares.org host
//   3. '' (relative) otherwise — local dev proxies /api to localhost:5000
const PRODUCTION_API = 'https://518cares-production.up.railway.app';
const isProdHost =
  typeof window !== 'undefined' &&
  /(^|\.)518cares\.org$/.test(window.location.hostname);
const API_URL = import.meta.env.VITE_API_URL || (isProdHost ? PRODUCTION_API : '');

const API_ENDPOINTS = {
  RESOURCES: {
    BASE: `${API_URL}/api/resources`,
    SEARCH: `${API_URL}/api/resources/search`,
    LOCATION: `${API_URL}/api/resources/location`,
    BY_TYPE: (typeId) => `${API_URL}/api/resources/type/${typeId}`,
    BY_ID: (id) => `${API_URL}/api/resources/${id}`,
    ELIGIBILITY: (id) => `${API_URL}/api/resources/${id}/eligibility`,
    TRANSIT_ROUTES: `${API_URL}/api/resources/transit-routes`,
    BLACKLIST: `${API_URL}/api/resources/blacklist`,
    BY_AREA: (area) => `${API_URL}/api/resources/area/${area}`,
  },
  ELIGIBILITY: {
    BASE: `${API_URL}/api/eligibility`,
    PROGRAMS: `${API_URL}/api/eligibility/programs`,
    RECOMMENDATIONS: `${API_URL}/api/eligibility/recommendations`,
  },
  TYPES: {
    BASE: `${API_URL}/api/types`,
  },
  AUTH: {
    LOGIN: `${API_URL}/api/login`,
    LOGOUT: `${API_URL}/api/logout`,
    ME: `${API_URL}/api/me`,
  },
  REPORTS: {
    PROBLEM: `${API_URL}/api/report-problem`,
  },
};

// HTTP status codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// Error messages
//
// Pass-9 follow-up: rewritten for a public, low-tech, often-frustrated
// audience. Persona testing (Margaret 68 / James 50, low-tech mobile)
// surfaced that the previous "You do not have permission to access this
// resource" copy made people think they'd done something wrong or that
// the site was broken, and they closed the tab. The site is public; no
// search response should ever read like an auth wall.
//
// Phrasing convention: no "resource", no "request", no "input". Address
// the user in first person, plain English, suggest what to do next.
const ERROR_MESSAGES = {
  NETWORK_ERROR: "We couldn't reach the server. Check your connection and try again.",
  SERVER_ERROR: "Something went wrong on our end. Please try again in a moment.",
  NOT_FOUND: "We couldn't find that. Try a different search, or browse all clinics.",
  UNAUTHORIZED: "Sign in to view this page.",
  FORBIDDEN: "We couldn't find that. Try a different search, or browse all clinics.",
  BAD_REQUEST: "We didn't understand that search. Try a simpler term like 'dentist' or 'pharmacy'.",
};

// API request helper
const apiRequest = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      switch (response.status) {
        case HTTP_STATUS.UNAUTHORIZED:
          throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
        case HTTP_STATUS.FORBIDDEN:
          throw new Error(ERROR_MESSAGES.FORBIDDEN);
        case HTTP_STATUS.NOT_FOUND:
          throw new Error(ERROR_MESSAGES.NOT_FOUND);
        case HTTP_STATUS.BAD_REQUEST:
          throw new Error(ERROR_MESSAGES.BAD_REQUEST);
        default:
          throw new Error(ERROR_MESSAGES.SERVER_ERROR);
      }
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
    }
    throw error;
  }
};

export { API_URL, API_ENDPOINTS, HTTP_STATUS, ERROR_MESSAGES, apiRequest }; 